import React from 'react';
import PropTypes from 'prop-types';
import {
    Input, UncontrolledButtonDropdown, DropdownToggle, DropdownItem, DropdownMenu,
    Row, Col, Label, FormGroup
} from 'reactstrap';
import { getFormattedCurrency } from '../components/utils';
import '../scss/_paymentCalculator.scss';
import arrowDown from '../images/icons/chevron-down.svg';

class PaymentCalculator extends React.Component {
    static contextTypes = {
        router: PropTypes.object.isRequired
    }
    constructor(props) {
        super(props);
        const listing = props.properties[0];
        //hoa amount from listing or 0
        const hoaAmount = listing.Hoafee || 0;
        const listPrice = listing.ListPrice;
        const numberOfYears = 30;
        const mortgageMonths = numberOfYears * 12;
        //defaulting to 20% down payment
        const downPayment = Math.trunc(listPrice * .2);
        const downPaymentPercentage = 20;
        const numberOfYearsDropdownOptions = [5, 10, 15, 20, 25, 30];
        //default value for home insurance is very rough guess based on quick google search
        const monthyInsurancePremium = Math.trunc((listPrice / 12) * .004);
        const mortgageInterestRate = 6.75;
        //property taxes from listing or zero
        const monthlyPropertyTax = listing.Taxes && !isNaN(parseFloat(listing.Taxes)) && isFinite(listing.Taxes) ? Math.trunc(listing.Taxes / 12) : 0;
        const pmiMip = 0;

        this.state = {
            listPrice: listPrice,
            numberOfYears: numberOfYears,
            numberOfYearsDropdownOptions: numberOfYearsDropdownOptions,
            downPayment: downPayment,
            monthyInsurancePremium: monthyInsurancePremium,
            mortgageInterestRate: mortgageInterestRate,
            monthlyPropertyTax: monthlyPropertyTax,
            pmiMip: pmiMip,
            hoaAmount: hoaAmount,
            mortgageMonths: mortgageMonths,
            downPaymentPercentage: downPaymentPercentage,
            pmiMipDollarAmount: '',
            liveMortgageRateUrl: props.liveMortgageUrlLink,
            pmiMipDisabled: true
        }

    }

    calculateEstimatedMonthlyPayment() {
        //Monthly Payment Formula
        //M = P [ i(1 + i)^n ] / [ (1 + i)^n – 1]

        //P = loan amount
        //i = monthly interest rate
        //n = number of months required to repay the loan

        let loanAmount = this.state.listPrice - this.state.downPayment;
        //if the loan amount is negative make it zero
        if (loanAmount < 0) {
            loanAmount = 0;
        }

        //based pmi on https://www.nerdwallet.com/article/mortgages/pmi-calculator
        const pmi = this.state.pmiMip > 0 ? (loanAmount / 12) * (this.state.pmiMip / 100) : 0;

        const monthlyInterestRate = (this.state.mortgageInterestRate / 100) / 12;
        const interestMultiplier = (monthlyInterestRate * Math.pow((1 + monthlyInterestRate), this.state.mortgageMonths)) / (Math.pow((1 + monthlyInterestRate), this.state.mortgageMonths) - 1);
        const principalAndInterest = monthlyInterestRate > 0 ? loanAmount * interestMultiplier : loanAmount / this.state.mortgageMonths;
        //add hoa fee, property tax, and insurance to monthly payment
        const hoaAmount = this.state.hoaAmount !== "" ? parseInt(this.state.hoaAmount) : 0;
        const monthlyPropertyTax = this.state.monthlyPropertyTax !== "" ? parseInt(this.state.monthlyPropertyTax) : 0;
        const monthlyInsurancePremium = this.state.monthyInsurancePremium !== "" ? parseInt(this.state.monthyInsurancePremium) : 0;

        const estimatedMonthlyPayment = principalAndInterest + hoaAmount + monthlyPropertyTax + monthlyInsurancePremium + pmi;
        if (this.props.onEstimatedPayment)
            this.props.onEstimatedPayment(getFormattedCurrency(estimatedMonthlyPayment));

        this.setState({ estimatedMonthlyPayment: estimatedMonthlyPayment, principalAndInterest: principalAndInterest, pmiMipDollarAmount: Math.trunc(pmi) });
    }

    componentDidMount() {
        this.calculateEstimatedMonthlyPayment();
    }

    handleInputChange = event => {
        const target = event.target;
        let value = target.type === 'checkbox' ? target.checked : target.value;
        const name = target.name;

        value = value === '.' ? '0.' : value;
        if ((!isNaN(parseFloat(value)) && isFinite(value)) || (value === '')) {
            if (name === 'downPaymentPercentage') {
                this.setState({
                    downPayment: this.state.listPrice * (value / 100),
                    pmiMipDisabled: value >= 20,
                    pmiMip: value < 20 ? this.state.pmiMip || 0.5 : 0
                })
            }
            else if (name === 'downPayment') {
                const percentage = (value / this.state.listPrice) * 100
                this.setState({
                    downPaymentPercentage: percentage.toFixed(2),
                    pmiMipDisabled: percentage.toFixed(2) >= 20,
                    pmiMip: percentage.toFixed(2) >= 20 ? 0 : this.state.pmiMip || 0.5
                })
            }

            this.setState({
                [name]: value
            }, () => { this.calculateEstimatedMonthlyPayment() });
        }
    }

    render() {
        return (<div className="col-12"><div className="calculator_container row">
            <div className="col-lg-5">
                <span className='calculation_result_box'>
                    <div className='calculation_result_box_contents'>
                        <h3>{getFormattedCurrency(this.state.estimatedMonthlyPayment)}/month</h3>
                        <div>Total Estimated Payment</div>
                        <h3>{getFormattedCurrency(this.state.principalAndInterest)}</h3>
                        <div>Monthly Principal & Interest</div>
                        <div dangerouslySetInnerHTML={{ __html: this.state.liveMortgageRateUrl }} />            
                    </div>
                </span>
            </div>
            <span className="col-lg-7">
                <Row form>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="listPrice">Purchase Price</Label>
                            <p className="dollar_icon">$</p>
                            <Input value={this.state.listPrice} onChange={this.handleInputChange} type="text" id="listPrice" name="listPrice" />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="downPayment">Down Payment</Label>
                            <Row form>
                                <Col>
                                    <p className="dollar_icon">$</p>
                                    <Input type="text" value={this.state.downPayment} onChange={this.handleInputChange} id="downPayment" name="downPayment" />
                                </Col>
                                <Col>
                                    <p className="percent_icon">%</p>
                                    <Input type="text" value={this.state.downPaymentPercentage} onChange={this.handleInputChange} name="downPaymentPercentage" aria-label="Down Payment Percentage" />
                                </Col>
                            </Row>
                        </FormGroup>
                    </Col>
                </Row>
                <Row form>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="mortgageInterestRate">Interest Rate</Label>
                            <p className="percent_icon">%</p>
                            <Input type="text" value={this.state.mortgageInterestRate} onChange={this.handleInputChange} id="mortgageInterestRate" name="mortgageInterestRate" />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="numberOfYears">Number of Years</Label>
                            <UncontrolledButtonDropdown id="numberOfYears" className={'dyna-dropdown calculation_input open'}>
                                <DropdownToggle caret size="sm">
                                    <span>{(this.state.numberOfYears !== "") ? this.state.numberOfYears : "30"}</span><img src={arrowDown} alt="arrowd" className="mg-lft-auto" />
                                </DropdownToggle>
                                <DropdownMenu>
                                    {this.state.numberOfYearsDropdownOptions.map
                                        ((e, i) => <DropdownItem key={i} onClick={() => { this.setState({ numberOfYears: e, mortgageMonths: e * 12 }, () => { this.calculateEstimatedMonthlyPayment() }) }}>{e}</DropdownItem>)
                                    }
                                </DropdownMenu>
                            </UncontrolledButtonDropdown>
                        </FormGroup>
                    </Col>
                </Row>
                <Row form>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="monthlyPropertyTax">Taxes</Label>
                            <p className="month_icon">/mo</p>
                            <Input value={this.state.monthlyPropertyTax} onChange={this.handleInputChange} type="text" id="monthlyPropertyTax" name="monthlyPropertyTax" />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="monthyInsurancePremium">Insurance</Label>
                            <p className="month_icon">/mo</p>
                            <Input value={this.state.monthyInsurancePremium} onChange={this.handleInputChange} type="text" id="monthyInsurancePremium" name="monthyInsurancePremium" />
                        </FormGroup>
                    </Col>
                </Row>
                <Row form>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="hoaAmount">Association Fees</Label>
                            <p className="month_icon">/mo</p>
                            <Input type="text" value={this.state.hoaAmount} onChange={this.handleInputChange} id="hoaAmount" name="hoaAmount" />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="pmiMip">PMI / MIP</Label>
                            <Row form>
                                <Col>
                                    <p className="percent_icon">%</p>
                                    <Input disabled={this.state.pmiMipDisabled} type="text" value={this.state.pmiMip} onChange={this.handleInputChange} id="pmiMip" name="pmiMip" />
                                </Col>
                                <Col>
                                    <p className="dollar_icon">$</p>
                                    <Input type="text" defaultValue={this.state.pmiMipDollarAmount} name="pmiMip" aria-label="PMI / MIP Dollar Amount" />
                                </Col>
                            </Row>
                            <span className='pmi_info_label'>
                                (Enter 0% to omit PMI/MIP from the calculation)
                                </span>
                        </FormGroup>
                    </Col>
                </Row>
            </span>
        </div></div>)
    }
}

export default PaymentCalculator;